import { useState, useEffect, useCallback, useMemo } from 'react';
import { AccessEntry, TEMPLATE_ACCESS } from '../utils';
import { GroupData, GroupedDataMap } from '../types';

/**
 * Custom hook for managing template access state and operations
 */
export const useTemplateAccess = (
  initialAccessList: AccessEntry[] | undefined | null = [],
  type: string,
) => {
  const [accessList, setAccessList] = useState<AccessEntry[]>(
    Array.isArray(initialAccessList) ? initialAccessList : [],
  );
  const [groupedData, setGroupedData] = useState<GroupedDataMap>({});

  // Memoized function to group access entries
  const groupAccessEntries = useCallback(
    (entries: AccessEntry[]): GroupedDataMap => {
      return entries?.reduce((acc: GroupedDataMap, curr) => {
        const groupKey = curr?.groupId;
        if (!acc?.[groupKey]) {
          acc[groupKey] = {
            isEveryone: curr?.isEveryone || false,
            AccessorIds: curr?.isEveryone ? null : [],
            ...(type === TEMPLATE_ACCESS
              ? { role: curr?.role }
              : { permission: curr?.permission }),
            groupId: curr?.groupId,
            ...(type !== TEMPLATE_ACCESS && { canEdit: curr?.canEdit }),
          };
        }
        acc?.[groupKey]?.AccessorIds?.push(curr?.AccessorId);
        return acc;
      }, {} as GroupedDataMap);
    },
    [],
  );

  // Update groupedData when accessList changes
  useEffect(() => {
    const grouped = groupAccessEntries(accessList);
    setGroupedData(grouped);
  }, [accessList, groupAccessEntries]);

  // Memoized updater function to update grouped data
  const updateGroupedData = useCallback(
    (groupId: string, newGroupData: GroupData) => {
      setGroupedData((prev) => ({
        ...prev,
        [groupId]: newGroupData,
      }));
    },
    [],
  );

  // Memoized function to add new group
  const addNewGroup = useCallback(
    (groupId: string, newGroupData: GroupData) => {
      setGroupedData((prev) => ({
        ...prev,
        [groupId]: newGroupData,
      }));
    },
    [],
  );

  // Memoized function to remove group
  const removeGroup = useCallback((groupId: string) => {
    setGroupedData((prev) => {
      const newGroupedData = { ...prev };
      delete newGroupedData?.[groupId];
      return newGroupedData;
    });
  }, []);

  // Memoized function to update access list
  const updateAccessList = useCallback((newAccessList: AccessEntry[]) => {
    setAccessList(newAccessList);
  }, []);

  // Memoized grouped data entries for rendering
  const groupedDataEntries = useMemo(
    () => Object.entries(groupedData),
    [groupedData],
  );

  // Memoized count of groups
  const groupCount = useMemo(
    () => Object.keys(groupedData)?.length ?? 0,
    [groupedData],
  );

  return {
    accessList,
    groupedData,
    groupedDataEntries,
    groupCount,
    setGroupedData,
    updateGroupedData,
    addNewGroup,
    removeGroup,
    updateAccessList,
  };
};
